import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Code, Minus,
} from "lucide-react";
import { adminAPI, absUrl } from "../services/api";
import "./RichTextEditor.css";

export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener", target: "_blank" } }),
      Image,
      Placeholder.configure({ placeholder: "Start writing your article…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep editor in sync if external value changes (e.g. loading existing article)
  React.useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const onUpload = useCallback(async (e) => {
    if (!editor) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const r = await adminAPI.upload(file);
      const src = absUrl(r.data.url);
      editor.chain().focus().setImage({ src, alt: file.name }).run();
    } catch (err) {
      alert("Image upload failed");
    }
  }, [editor]);

  if (!editor) return <div className="rte-loading">Loading editor…</div>;

  const btn = (active, onClick, Icon, label, testId) => (
    <button
      type="button"
      onClick={onClick}
      className={`rte-btn ${active ? "is-active" : ""}`}
      title={label}
      data-testid={testId}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div className="rte">
      <div className="rte-toolbar" data-testid="rte-toolbar">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), Bold, "Bold", "rte-bold")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), Italic, "Italic", "rte-italic")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, "Heading 2", "rte-h2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Heading3, "Heading 3", "rte-h3")}
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), List, "Bullet list", "rte-ul")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, "Numbered list", "rte-ol")}
        {btn(editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), Quote, "Quote", "rte-quote")}
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), Minus, "Divider", "rte-hr")}
        {btn(editor.isActive("link"), setLink, LinkIcon, "Link", "rte-link")}
        {btn(editor.isActive("code"), () => editor.chain().focus().toggleCode().run(), Code, "Inline code", "rte-code")}
        <label className="rte-btn" title="Insert image" data-testid="rte-image">
          <ImageIcon size={16} />
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={onUpload} />
        </label>
        <span className="rte-divider" />
        {btn(false, () => editor.chain().focus().undo().run(), Undo2, "Undo", "rte-undo")}
        {btn(false, () => editor.chain().focus().redo().run(), Redo2, "Redo", "rte-redo")}
      </div>
      <EditorContent editor={editor} className="rte-content" />
    </div>
  );
}
